import { AlertTriangle, BarChart3, FileText, Mail, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "@/lib/auth";

export function Login() {
  const { status, login } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (status === "signed-in") {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(username.trim(), password);
    } catch (reason) {
      setError(reason instanceof Error && /credentials|401|No active/i.test(reason.message)
        ? "That username and password don’t match an active account."
        : reason instanceof Error
          ? reason.message
          : "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <section className="login__art" aria-hidden="true">
        <div>
          <p className="hero__eyebrow">Aviox Blogs · Newsroom</p>
        </div>
        <div>
          <h2>Every story, from first link to inbox.</h2>
          <p>
            Review what the agents found, shape the three tabs, publish to the site and send it to subscribers — each
            role sees exactly the tools it needs.
          </p>
        </div>
        <div className="login__features">
          <span className="login__feature">
            <FileText size={18} /> Content workspace with review queue
          </span>
          <span className="login__feature">
            <Mail size={18} /> Newsletter issues and subscribers
          </span>
          <span className="login__feature">
            <BarChart3 size={18} /> Dashboards tailored to every role
          </span>
          <span className="login__feature">
            <ShieldCheck size={18} /> Super Admin, Admin, Editor and Viewer access
          </span>
        </div>
      </section>

      <section className="login__panel">
        <form className="login__form" onSubmit={onSubmit} noValidate>
          <img src="/brand/logo.png" alt="Aviox Blogs" />
          <div>
            <h1>Sign in to the Newsroom</h1>
            <p className="muted">Use the account a Super Admin created for you.</p>
          </div>

          {error && (
            <div className="alert alert--bad" role="alert">
              <AlertTriangle size={18} /> {error}
            </div>
          )}

          <label className="field">
            <span className="field__label">Username</span>
            <input
              className="input"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="field">
            <span className="field__label">Password</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>

          <button className="btn btn--primary full" style={{ height: 44 }} disabled={busy || !username || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </section>
    </div>
  );
}
